import React, { useState, useEffect } from "react";
import { Table, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  fetchAllWeeklyTimesheets,
  saveTeamDayEntry,
  showGeneralError,
  createOrUpdateTeamWeeklyTimesheet,
  saveTeamComment,
  deleteTeamWeeklyTimesheetEntry,
  exportTeamWeeklyTimesheet,
} from "actions";
import AddCommentModal from "../AddCommentModal";
import { Filter, InformationMsg } from "../";
import { DeleteModal, Excel } from "CommonComponent";
import { isEmpty } from "lodash";
import moment from "moment";
import TimeInputCustom from "../TimeInputCustom";

const List = ({
  extraFiltersState,
  setExtraFiltersState,
  currentWeek,
  setCurrentWeek,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();

  const session = useSelector((s) => s.session);
  const weeklyTimesheetState = useSelector(
    (state) => state.weeklyTimesheetState
  );
  const object_viewed_id = session?.current_page_permissions?.object_id || "";

  const [data, setData] = useState([]);

  const [days, setDays] = useState([]);
  const [dates, setDates] = useState([]);
  const [commentItem, setCommentItem] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [weekValue, setWeekValue] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const weekEndDate = params.get("week_end_date");
    const programId = params.get("program_id");
    const userIds = params.get("user_ids");

    if (weekEndDate) {
      const newValues = {
        ...extraFiltersState.values,
        week_end_date: weekEndDate,
      };
      if (programId) {
        newValues.program_id = [programId];
      }
      if (userIds) {
        newValues.user_ids = userIds.split(","); // Assuming user_ids can be multiple, comma-separated
      }
      setExtraFiltersState((prev) => ({ ...prev, values: newValues }));
      filterTable(newValues);
    }
  }, [location.search]);

  /*useEffect(() => {
    dispatch({ type: 'CLEAR_WEEKLY_TIMESHEETS' }); // Clear the timesheet data
    setDates([]);
    setDays([]);
  
  }, [extraFiltersState.values.week_end_date]);*/

  useEffect(() => {
    if (weeklyTimesheetState.redirectToList) {
      if (!isEmpty(extraFiltersState.values.week_end_date)) {
        filterTable();
      }
    }
  }, [
    weeklyTimesheetState.redirectToList,
    extraFiltersState.values.week_end_date,
  ]);

  useEffect(() => {
    const weeklyTimesheets = weeklyTimesheetState.weeklyTimesheetsList;

    if (!Array.isArray(weeklyTimesheets) || weeklyTimesheets.length === 0) {
      setData([]);
      return;
    }

    const formattedData = [];

    weeklyTimesheets.forEach((programData) => {
      let isFirstTaskOfProgram = true;

      if (!Array.isArray(programData.users)) return;

      programData.users.forEach((userBlock) => {
        if (!Array.isArray(userBlock.tasks)) return;

        userBlock.tasks.forEach((taskItem) => {
          const processEntry = (entry, isOverTime) => {
            if (!entry) return;

            const dailyValues = Array.from({ length: 7 }, () => ({
              hours: "",
              minutes: "",
            }));

            if (Array.isArray(entry.details)) {
              entry.details.forEach((detail) => {
                const dayIndex = new Date(
                  detail.work_date + "T00:00:00"
                ).getDay();

                // Monday = 0
                const adjustedIndex = dayIndex === 0 ? 6 : dayIndex - 1;

                if (adjustedIndex >= 0 && adjustedIndex < 7) {
                  dailyValues[adjustedIndex] = {
                    hours: Math.floor(detail.hours).toString(),
                    minutes: Math.round(detail.minutes).toString(),
                  };
                }
              });
            }

            formattedData.push({
              program: isFirstTaskOfProgram
                ? programData.program?.program_name || ""
                : "",
              user_name: userBlock.user?.name || "N/A",
              category: taskItem.task?.category?.name || "N/A",
              task: taskItem.task?.name || "",
              values: dailyValues,
              program_id: programData.program?.id,
              task_id: taskItem.task?.id,
              category_id: taskItem.task?.category?.id || null,
              weekly_timesheet_id: entry.id,
              user_id: entry.user?.id,
              total: parseFloat(entry.total_hours || 0),
              is_over_time_entry: isOverTime,
              comment: entry.comments || "",
            });

            isFirstTaskOfProgram = false;
          };

          // Main entry
          processEntry(taskItem.main_entry, false);

          // Overtime entry
          processEntry(taskItem.overtime_entry, true);
        });
      });
    });

    setData(formattedData);
  }, [weeklyTimesheetState.weeklyTimesheetsList]);

  const filterTable = (filters) => {
    const filterValues = filters || extraFiltersState.values;
    if (!isEmpty(filterValues.week_end_date)) {
      const weekEndDate = moment(filterValues.week_end_date);
      const weekStartDate = weekEndDate.clone().subtract(6, "days");
      const newDates = [];
      const newDays = [];
      let currentDate = weekStartDate;
      while (currentDate.isSameOrBefore(weekEndDate)) {
        newDates.push(currentDate.format("M/DD"));
        newDays.push(currentDate.format("ddd").toUpperCase());
        currentDate.add(1, "days");
      }
      setDates(newDates);
      setDays(newDays);

      dispatch(
        fetchAllWeeklyTimesheets({
          ...filterValues,
          object_viewed_id,
        })
      );
    } else {
      dispatch({ type: "CLEAR_WEEKLY_TIMESHEETS" });
      setDates([]);
      setDays([]);
    }
  };

  useEffect(() => {
    if (currentWeek) {
      filterTable();
    }
  }, [currentWeek]);

  const handleValueChange = (rowIndex, dayIndex, { field, value }) => {
    const updatedData = [...data];
    updatedData[rowIndex].values[dayIndex][field] = value;

    // calculate total hours
    updatedData[rowIndex].total = updatedData[rowIndex].values.reduce(
      (sum, v) => {
        const hours = parseInt(v.hours || 0);
        const minutes = parseInt(v.minutes || 0);
        return sum + hours + minutes / 60;
      },
      0
    );

    setData(updatedData);
  };

  const handleShiftTimeUpdate = (rowIndex, dayIndex) => {
    const { hours, minutes } = data[rowIndex].values[dayIndex];
    const item = data[rowIndex];

    // Calculate the work date based on dayIndex and week_end_date
    if (!extraFiltersState.values.week_end_date) {
      showGeneralError("Week end date is required to save day entry");
      return;
    }

    const weekEndDate = moment(extraFiltersState.values.week_end_date);
    const weekStartDate = weekEndDate.clone().subtract(6, "days");
    const workDate = weekStartDate.clone().add(dayIndex, "days");

    // Calculate total hours (hours + minutes/60)
    const hoursValue =
      hours === "" || hours === null || hours === undefined
        ? 0
        : parseFloat(hours || 0);
    const minutesValue =
      minutes === "" || minutes === null || minutes === undefined
        ? 0
        : parseFloat(minutes || 0);
    const totalHours = hoursValue + minutesValue / 60;

    // Prepare data for API
    const dayEntryData = {
      work_date: workDate.format("YYYY-MM-DD"),
      total_time: totalHours,
      minutes: minutesValue,
      hours: hoursValue,
      user_id: item.user_id,
    };

    if (!item.weekly_timesheet_id) {
      showGeneralError("Weekly timesheet ID is required to save day entry");
      return;
    }

    dispatch(
      saveTeamDayEntry(item.weekly_timesheet_id, dayEntryData, object_viewed_id)
    );
  };

  const addOverTime = (e, item) => {
    e.preventDefault();
    const overTimeData = {
      program_id: item.program_id,
      task_ids: [item.task_id],
      is_over_time_entry: true,
      week_end_date: extraFiltersState.values.week_end_date,
      user_id: item.user_id,
    };
    dispatch(
      createOrUpdateTeamWeeklyTimesheet(overTimeData, "POST", object_viewed_id)
    );
  };

  const handleAddComment = (comment) => {
    if (commentItem) {
      const commentData = { comment: comment };
      dispatch(
        saveTeamComment(
          commentItem.weekly_timesheet_id,
          commentData,
          object_viewed_id
        )
      )
        .then(() => {
          const updatedData = data.map((item) => {
            if (item.weekly_timesheet_id === commentItem.weekly_timesheet_id) {
              return { ...item, comment: comment };
            }
            return item;
          });
          setData(updatedData);
          setShowComment(false);
          setCommentItem(null);
        })
        .catch(() => {
          // Error is already handled by the action
        });
    }
  };

  const [showComment, setShowComment] = useState(false);

  const handleTeamDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmTeamDelete = () => {
    if (!deleteItem) return;
    dispatch(
      deleteTeamWeeklyTimesheetEntry(
        deleteItem.weekly_timesheet_id,
        object_viewed_id
      )
    )
      .then(() => {
        setShowDeleteModal(false);
        setDeleteItem(null);
        // Refresh the table after deletion
        if (!isEmpty(extraFiltersState.values.week_end_date)) {
          filterTable();
        }
      })
      .catch(() => {
        // Error is already handled by the action
        setShowDeleteModal(false);
        setDeleteItem(null);
      });
  };

  const handleExport = () => {
    dispatch(
      exportTeamWeeklyTimesheet(
        {
          user_ids: extraFiltersState.values.user_ids,
          program_id: extraFiltersState.values.program_id,
          task_ids: extraFiltersState.values.task_ids,
          week_end_date: extraFiltersState.values.week_end_date, // IMPORTANT
        },
        object_viewed_id
      )
    );
  };

  const hasTimeValue = (value) => {
    return (
      (value.hours && value.hours !== "") ||
      (value.minutes && value.minutes !== "")
    );
  };

  return (
    <>
      <div className="d-flex align-items-center justify-content-between p-4">
        <InformationMsg />

        <Excel handleExport={handleExport} />
      </div>
      <Filter
        onFilter={filterTable}
        extraFiltersState={extraFiltersState}
        setExtraFiltersState={setExtraFiltersState}
        currentWeek={currentWeek}
        setCurrentWeek={setCurrentWeek}
        weekValue={weekValue}
        setWeekValue={setWeekValue}
      />
      <div className="weekly-table-container td-border">
        <Table className="no-border">
          <thead>
            <tr>
              <th>Program Name</th>
              <th>User</th>
              <th>Category</th>
              <th>Task</th>
              {dates.map((date, index) => (
                <th key={index}>
                  {date} <br />{" "}
                  <span className="days-table">({days[index]})</span>
                </th>
              ))}
              <th>Total</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, rowIndex) => (
              <tr
                key={rowIndex}
                className={item.is_over_time_entry ? "over-time-entry" : ""}
              >
                <td className="program-name">{item.program}</td>
                <td>{item.user_name}</td>
                <td>{item.category}</td>
                <td>{item.task}</td>
                {item.values.map((value, dayIndex) => (
                  <td key={dayIndex}>
                    <TimeInputCustom
                      value={value}
                      rowIndex={rowIndex}
                      dayIndex={dayIndex}
                      handleValueChange={handleValueChange}
                      handleShiftTimeUpdate={handleShiftTimeUpdate}
                      hasTime={hasTimeValue(value)}
                    />
                  </td>
                ))}
                <td>{item.total}</td>
                <td>
                  <div className="d-flex align-items-center">
                    {!item.is_over_time_entry ? (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`overtime-tooltip-${rowIndex}`}>
                            Add Overtime
                          </Tooltip>
                        }
                      >
                        <img
                          src="/assets/images/time-icon.svg"
                          alt="Add Over Time"
                          className="me-2"
                          style={{ cursor: "pointer" }}
                          onClick={(e) => addOverTime(e, item)}
                        />
                      </OverlayTrigger>
                    ) : (
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip id={`overtime-tooltip-${rowIndex}`}>
                            Add Overtime
                          </Tooltip>
                        }
                      >
                        <img
                          src="/assets/images/time-icon.svg"
                          alt="Overtime"
                          className="me-2"
                          style={{ cursor: "pointer" }}
                        />
                      </OverlayTrigger>
                    )}
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`comment-tooltip-${rowIndex}`}>
                          {item.comment ? item.comment : "Add Comment"}
                        </Tooltip>
                      }
                    >
                      <img
                        src="/assets/images/comment-icon.svg"
                        alt="Comment"
                        onClick={() => {
                          setCommentItem(item);
                          setShowComment(true);
                        }}
                        style={{ cursor: "pointer" }}
                        className="me-2"
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id={`delete-tooltip-${rowIndex}`}>
                          Delete Entry
                        </Tooltip>
                      }
                    >
                      <img
                        src="/assets/images/delete-icon.svg"
                        alt="Delete"
                        onClick={() => handleTeamDelete(item)}
                        style={{ cursor: "pointer" }}
                      />
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {/* comment modal */}
        <AddCommentModal
          show={showComment}
          handleClose={() => {
            setShowComment(false);
            setCommentItem(null);
          }}
          taskComment={commentItem?.comment || ""}
          handleAdd={handleAddComment}
          dates={dates}
        />

        {/* delete confirmation modal */}
        {showDeleteModal && (
          <DeleteModal
            show={showDeleteModal}
            onClose={() => {
              setShowDeleteModal(false);
              setDeleteItem(null);
            }}
            onDelete={confirmTeamDelete}
            message="Are you sure you want to delete this timesheet entry?"
          />
        )}
      </div>
    </>
  );
};

export default List;
